<template>
  <div v-if="pageLoading">
    <p-o-s-page-skeleton variant="bills" />
  </div>
  <div v-else-if="!hasPermission">
    <p-o-s-access-denied />
  </div>
  <div v-else>
    <!-- Summary cards (scope: shift ปัจจุบัน) -->
    <div class="row g-2 mb-3">
      <div class="col-6 col-md-3">
        <div class="card summary-card border-0 rounded-3 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">
              <i class="fa-solid fa-receipt me-1 text-theme"/>{{ $t('Total Bills') }}
            </div>
            <div class="fs-4 fw-bold">{{ summary.total_bills }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card summary-card border-0 rounded-3 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">
              <i class="fa-solid fa-sack-dollar me-1 text-success"/>{{ $t('Total Sales') }}
            </div>
            <div class="fs-4 fw-bold text-success">
              {{ $storeIndex.formatThaiCurrency(summary.total_sales) }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card summary-card border-0 rounded-3 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">
              <i class="fa-solid fa-hourglass-half me-1 text-warning"/>{{ $t('Unpaid') }}
            </div>
            <div class="fs-4 fw-bold text-warning">{{ summary.unpaid }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card summary-card border-0 rounded-3 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">
              <i class="fa-solid fa-rotate-left me-1 text-danger"/>{{ $t('Refunded') }}
            </div>
            <div class="fs-4 fw-bold text-danger">{{ summary.refunded }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toolbar: search + filters (status/source ย้ายมาอยู่ใน Filters offcanvas แทน viewpoint tabs) -->
    <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-2 mb-3">
      <div class="input-group input-group-sm search-input-wrapper">
        <input
          type="search"
          class="form-control shadow-none"
          :placeholder="$t('Search bill no.') + '...'"
          v-model.trim="params.search"
          @keyup.enter="resetAndFetch"
        >
        <button class="input-group-text bg-white" type="button" @click="resetAndFetch">
          <i class="fas fa-search"/>
        </button>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary fw-semibold rounded-3 text-nowrap position-relative"
          @click="offcanvasFilter.open = true"
        >
          <i class="fa-solid fa-filter me-1"/>{{ $t('Filters') }}
          <span v-if="activeFilterCount" class="badge bg-theme ms-1 rounded-pill">
            {{ activeFilterCount }}
          </span>
        </button>
      </div>
    </div>

    <!-- Datatable (POS-styled) -->
    <div class="pos-table-card">
      <vue3-easy-data-table
        ref="dataTable"
        v-model:server-options="datatable.serverOptions"
        :headers="headers"
        :items="datatable.items"
        :loading="datatable.loading"
        :server-items-length="datatable.total"
        :theme-color="themeColor"
        :rows-per-page="params.limit"
        :hide-rows-per-page="true"
        :empty-message="$t('No bills in this shift')"
        :table-min-height="null"
        :body-item-class-name="'align-middle py-2 px-2'"
        buttons-pagination
        table-class-name="pos-datatable"
      >
        <template #header-total="header">
          <span class="w-100 text-end">{{ header.text }}</span>
        </template>
        <template #header-action="header">
          <span class="w-100 text-end">{{ header.text }}</span>
        </template>

        <template #item-bill_no="item">
          <div class="fw-bold">#{{ item.bill_no }}</div>
          <div class="text-muted small">{{ item.created_by || 'System' }}</div>
        </template>

        <template #item-source="item">
          <span v-if="firstSource(item)" class="badge source-pill rounded-pill">
            <i :class="sourceIcon(firstSource(item))" class="me-1"/>{{ $t(sourceLabel(firstSource(item))) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #item-ref="item">
          <div v-if="item.table_name">
            <i class="fa-solid fa-chair me-1 text-muted"/>
            <span class="fw-semibold">{{ item.table_name }}</span>
          </div>
          <div v-else-if="item.customer_name">
            <i class="fa-solid fa-user me-1 text-muted"/>{{ item.customer_name }}
          </div>
          <div v-else class="text-muted">{{ $t('Walk-in') }}</div>
        </template>

        <template #item-created_at="item">
          {{ formatTime(item.created_at) }}
        </template>

        <template #item-payment_status="item">
          <span
            class="badge rounded-pill"
            :class="'bg-' + statusColor(item.payment_status) + '-subtle text-' + statusColor(item.payment_status) + '-emphasis'"
          >
            {{ $t(statusLabel(item.payment_status)) }}
          </span>
          <div v-if="toNumber(item.refunded_amount) > 0" class="text-danger small mt-1">
            <i class="fa-solid fa-rotate-left me-1"/>{{ $storeIndex.formatThaiCurrency(toNumber(item.refunded_amount)) }}
          </div>
        </template>

        <template #item-total="item">
          <div
            class="text-end fw-bold"
            :class="item.payment_status === 'refunded' ? 'text-danger text-decoration-line-through' : ''"
          >
            {{ $storeIndex.formatThaiCurrency(toNumber(item.total)) }}
          </div>
        </template>

        <template #item-action="item">
          <div class="icon-link custom-actions justify-content-end">
            <popper :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon" @click="openBill(item)">
                <i class="fa-solid fa-eye"/>
              </button>
              <template #content><div>{{ $t('View') }}</div></template>
            </popper>

            <popper v-if="canCollect(item)" :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
              <button type="button" class="btn btn-success btn-sm btn-icon" @click="openCollect(item)">
                <i class="fa-solid fa-money-bill-wave"/>
              </button>
              <template #content><div>{{ $t('Collect Payment') }}</div></template>
            </popper>

            <popper v-if="canRefund(item)" :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
              <button type="button" class="btn btn-outline-warning btn-sm btn-icon" @click="openRefund(item)">
                <i class="fa-solid fa-rotate-left"/>
              </button>
              <template #content><div>{{ $t('Refund') }}</div></template>
            </popper>

            <!--
              ลูกค้าขอใบกำกับเต็มรูปที่เคาน์เตอร์ — เดิมทำได้แต่หลังร้าน ซึ่งลูกค้ายืนรอไม่ได้
              (api/docs/vat-compliance.md D11) ใบย่อที่พิมพ์ไปแล้วยังคงอยู่ ใบเต็มรูปออก "แทน"
            -->
            <popper v-if="canIssueTaxInvoice(item)" :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
              <button type="button" class="btn btn-outline-primary btn-sm btn-icon" @click="openTaxInvoice(item)">
                <i class="fa-solid fa-file-invoice"/>
              </button>
              <template #content><div>{{ $t('Issue Full Tax Invoice') }}</div></template>
            </popper>

            <popper :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm btn-icon"
                :disabled="printingId === item.id"
                @click="quickPrint(item)"
              >
                <i :class="printingId === item.id ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-print'"/>
              </button>
              <template #content><div>{{ $t('Print Bill') }}</div></template>
            </popper>

            <popper v-if="canVoid(item)" :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
              <button type="button" class="btn btn-outline-danger btn-sm btn-icon" @click="quickVoid(item)">
                <i class="fa-solid fa-ban"/>
              </button>
              <template #content><div>{{ $t('Void Bill') }}</div></template>
            </popper>
          </div>
        </template>

        <template #loading>
          <i class="fa-solid fa-spinner fa-spin fa-2xl"/>
        </template>
      </vue3-easy-data-table>
    </div>

    <offcanvas-info
      v-if="offcanvasInfo.open"
      ref="offcanvasInfoRef"
      :bill-id="offcanvasInfo.billId"
      @close="offcanvasInfo.open = false"
      @collect="openCollectById(offcanvasInfo.billId)"
      @refund="openRefundById(offcanvasInfo.billId)"
      @print="quickPrintById(offcanvasInfo.billId)"
      @void="quickVoidById(offcanvasInfo.billId)"
    />

    <!-- รับเงิน: ใช้ UI เดียวกับหน้า session (OffcanvasPayment) ยิงเข้า POSBills.payment ของ shift นี้ -->
    <offcanvas-payment
      v-if="paymentOffcanvas.open"
      ref="paymentRef"
      :bill="paymentOffcanvas.bill"
      :payment-methods="paymentOffcanvas.paymentMethods"
      :session-name="paymentOffcanvas.name"
      :submitting="paymentOffcanvas.submitting"
      @close="paymentOffcanvas.open = false"
      @confirmed="handlePaymentConfirmed"
    />

    <modal-tax-invoice
      v-if="taxInvoiceModal.open"
      ref="taxInvoiceModalRef"
      :bill="taxInvoiceModal.bill"
      :shop-tax="shopTax"
      @close="taxInvoiceModal.open = false"
      @confirm="handleTaxInvoiceConfirm"
    />

    <offcanvas-refund
      v-if="refundModal.open"
      ref="refundModalRef"
      :bill="refundModal.bill"
      @close="refundModal.open = false"
      @confirm="handleRefundConfirm"
    />

    <offcanvas-filter
      v-if="offcanvasFilter.open"
      :sections="filterSections"
      :value="{
        status_ids: params.status_ids,
        source_ids: params.source_ids
      }"
      @apply="onApplyFilter"
      @close="offcanvasFilter.open = false"
    />
  </div>
</template>

<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import POSPageSkeleton from '@/plugins/POSPageSkeleton.vue';
import POSAccessDenied from '@/plugins/POSAccessDenied.vue';
import Popper from 'vue3-popper';
import moment from 'moment';
import OffcanvasInfo from './OffcanvasInfo.vue';
import OffcanvasFilter from '@/components/shared/OffcanvasFilter.vue';
import OffcanvasPayment from '@/components/shared/billing/OffcanvasPayment.vue';
import OffcanvasRefund from './OffcanvasRefund.vue';
// ใช้ modal ตัวเดียวกับหลังร้าน — ฟอร์มข้อมูลลูกค้าสำหรับใบกำกับเต็มรูปเหมือนกันทุกอย่าง
import ModalTaxInvoice from '@/components/bills/ModalTaxInvoice.vue';
import { POSBills } from '@/services/endpoint/pos.service';
import {
  buildPromptPayQrImage,
  buildReviewQrImage,
  buildReceiptDocDefinition,
  preparePrintConfig,
  printPdf
} from '@/utils/billReceipt';
import { buildTaxInvoiceA4DocDefinition } from '@/utils/taxInvoiceReceipt';
import { isSupported as escposIsSupported, printCanvas, renderDocDefToCanvas, dotsForPaperWidth } from '@/utils/escposPrinter';
import { SOURCE_ICONS, STATUS_COLORS, humanize } from '@/components/bills/constants.js';

export default {
  name: 'POSBills',
  components: {
    Vue3EasyDataTable,
    POSPageSkeleton,
    POSAccessDenied,
    Popper,
    ModalTaxInvoice,
    OffcanvasInfo,
    OffcanvasFilter,
    OffcanvasPayment,
    OffcanvasRefund
  },
  data() {
    return {
      pageLoading: false,
      printingId: null,
      hasPermission: true,
      themeColor: '#E96C15',
      params: {
        limit: 10,
        page: 1,
        search: '',
        status_ids: [],
        source_ids: []
      },
      taxInvoiceModal: { open: false, bill: null },
      // ค่าภาษีของร้าน (filter.shop_tax) — modal ใช้โชว์เลขผู้เสียภาษีของร้าน
      shopTax: { tax_policy: 'no_tax', vat_rate: 0, tax_id: '', tax_entity_name: '' },
      filters: {
        payment_statuses: [],
        sources: []
      },
      summary: { total_bills: 0, total_sales: 0, unpaid: 0, refunded: 0 },
      datatable: {
        loading: false,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10,
          sortBy: 'created_at',
          sortType: 'desc'
        }
      },
      offcanvasInfo:    { open: false, billId: null },
      paymentOffcanvas: { open: false, bill: null, paymentMethods: [], name: '', submitting: false },
      refundModal:      { open: false, bill: null },
      offcanvasFilter:  { open: false }
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('Bill No.'),          value: 'bill_no',        width: 160 },
        { text: this.$t('Source'),            value: 'source',         width: 130 },
        { text: this.$t('Table / Customer'),  value: 'ref',            width: 180 },
        { text: this.$t('Opened At'),         value: 'created_at',     width: 150, sortable: true },
        { text: this.$t('Status'),            value: 'payment_status', width: 150 },
        { text: this.$t('Total'),             value: 'total',          width: 130 },
        { text: this.$t('Action'),            value: 'action',         width: 200 }
      ];
    },
    sourceTypes() {
      return (this.filters.sources || []).map(s => ({
        value: s.text,
        label: s.name,
        icon: SOURCE_ICONS[s.text] || 'fa-solid fa-receipt'
      }));
    },
    statusTypes() {
      return (this.filters.payment_statuses || []).map(s => ({
        value: s.text,
        label: s.name
      }));
    },
    activeFilterCount() {
      return this.params.status_ids.length + this.params.source_ids.length;
    },
    filterSections() {
      return [
        {
          key: 'status_ids',
          label: 'Status',
          icon: 'fa-circle-info',
          options: (this.filters.payment_statuses || []).map(s => ({ id: s.text, name: s.name }))
        },
        {
          key: 'source_ids',
          label: 'Source',
          icon: 'fa-store',
          options: (this.filters.sources || []).map(s => ({ id: s.text, name: s.name }))
        }
      ];
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    buildQuery() {
      const p = this.params;
      const query = {
        viewpoint: 'all',
        page: p.page,
        limit: p.limit
      };
      if (p.search) query.search = p.search;
      // filter เป็น checkbox เลือกได้หลายค่า — ส่งทั้ง array ($.param แตกเป็น payment_status[]=...)
      if (p.status_ids && p.status_ids.length) query.payment_status = [...p.status_ids];
      if (p.source_ids && p.source_ids.length) query.source = [...p.source_ids];
      const { sortBy, sortType } = this.datatable.serverOptions;
      if (sortBy === 'created_at' && sortType) {
        query.sort = 'created_at|' + sortType;
      } else {
        query.sort = 'created_at|desc';
      }
      return query;
    },
    async fetchData() {
      this.datatable.loading = true;
      try {
        const queryString = '?' + $.param(this.buildQuery());
        const res = await POSBills.list(queryString);
        this.datatable.items = res.data.data || [];
        this.datatable.total = res.data.meta?.total || 0;
        if (res.data.summary) {
          this.summary = { ...this.summary, ...res.data.summary };
        }
        if (res.data.filter) {
          this.shopTax = res.data.filter.shop_tax || this.shopTax;
          this.filters.payment_statuses = res.data.filter.payment_statuses || [];
          this.filters.sources = res.data.filter.sources || [];
        }
        this.hasPermission = true;
      } catch (err) {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.pageLoading = false;
        }
      }
      this.datatable.loading = false;
    },
    resetAndFetch() {
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    onApplyFilter(payload) {
      this.params.status_ids = payload.status_ids || [];
      this.params.source_ids = payload.source_ids || [];
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    formatTime(value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm') : '-';
    },
    toNumber(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    firstSource(item) {
      const s = item.sources;
      if (Array.isArray(s)) return s[0] || '';
      return s || '';
    },
    sourceIcon(v) {
      return SOURCE_ICONS[v] || 'fa-solid fa-receipt';
    },
    sourceLabel(v) {
      const s = this.sourceTypes.find(x => x.value === v);
      return s ? s.label : humanize(v);
    },
    statusLabel(v) {
      const s = this.statusTypes.find(x => x.value === v);
      return s ? s.label : humanize(v);
    },
    statusColor(v) {
      return STATUS_COLORS[v] || 'secondary';
    },
    // บิล platform: เงินจบบนแอปแล้ว — ห้ามรับเงิน/คืนเงิน/void จากหน้านี้
    // (settle อัตโนมัติตอน "เสร็จสิ้น" / ยกเลิกผ่านหน้า ออเดอร์ Platform)
    canCollect(item) {
      if (item.is_platform_bill) return false;
      return item.payment_status === 'unpaid' || item.payment_status === 'partially_paid';
    },
    canRefund(item) {
      if (item.voided_at || item.is_platform_bill) return false;
      return item.payment_status === 'paid' || item.payment_status === 'partially_refunded';
    },
    /**
     * ออกใบกำกับเต็มรูปได้เมื่อ ร้านจด VAT + บิลจ่ายเงินแล้ว + ยังไม่เคยออก
     * (ใบย่อที่พิมพ์ไปแล้วไม่หาย ใบเต็มรูปออก "แทน" — vat-compliance.md D9)
     */
    canIssueTaxInvoice(item) {
      if (!item || item.voided_at) return false;
      // ใบกำกับเต็มรูปต้องมีในแพ็ก (feature_tax_full) — ไม่มีก็ซ่อนปุ่ม ตัวบังคับจริงอยู่ที่ API
      if (!this.$hasFeature('feature_tax_full')) return false;
      if (item.tax_invoice_id !== '' && item.tax_invoice_id != null) return false;
      return !!this.shopTax.tax_policy
        && this.shopTax.tax_policy !== 'no_tax'
        && item.payment_status === 'paid';
    },
    canVoid(item) {
      if (!item || item.voided_at || item.is_platform_bill) return false;
      return item.payment_status === 'unpaid';
    },
    openBill(item) {
      this.offcanvasInfo.billId = item.id;
      this.offcanvasInfo.open = true;
    },
    closeOffcanvasInfo() {
      if (this.$refs.offcanvasInfoRef) {
        this.$refs.offcanvasInfoRef.hide();
      } else {
        this.offcanvasInfo.open = false;
      }
    },
    async loadAndOpen(billId, target) {
      try {
        const res = await POSBills.info(billId);
        target.bill = res.data.data;
        target.open = true;
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      }
    },
    // --- Collect payment (session-style OffcanvasPayment) ---
    async openCollect(item) {
      if (!this.canCollect(item)) return;
      this.closeOffcanvasInfo();
      await this.loadPayment(item.id);
    },
    async openCollectById(billId) {
      if (!billId) return;
      this.closeOffcanvasInfo();
      await this.loadPayment(billId);
    },
    async loadPayment(billId) {
      try {
        const res = await POSBills.getPayment(billId);
        const bill = res.data.data || {};
        // OffcanvasPayment อ่าน grand_total เป็นยอดที่ต้องชำระ — bill ฝั่งนี้ใช้ field `total`
        this.paymentOffcanvas.bill = { ...bill, grand_total: this.toNumber(bill.total) };
        this.paymentOffcanvas.paymentMethods = res.data.payment_methods || [];
        this.paymentOffcanvas.name = bill.table_name || bill.customer_name || ('#' + bill.bill_no);
        this.paymentOffcanvas.submitting = false;
        this.paymentOffcanvas.open = true;
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      }
    },
    async handlePaymentConfirmed(payload) {
      const bill = this.paymentOffcanvas.bill;
      if (!bill || !payload?.payment?.length) return;
      this.paymentOffcanvas.submitting = true;
      try {
        // หน้านี้ไม่ได้แก้ส่วนลด/ค่าส่ง — ส่งเฉพาะ payment[] (discount/shipping จาก OffcanvasPayment เป็น no-op อยู่แล้ว)
        await POSBills.payment(bill.id, { payment: payload.payment });
        // ปิดผ่าน Bootstrap (ref.hide) เพื่อให้ backdrop/เงาถูกลบด้วย — ตัว @close จะ set open=false ให้เอง
        if (this.$refs.paymentRef) this.$refs.paymentRef.hide();
        else this.paymentOffcanvas.open = false;
        this.$toast.success('<b>' + this.$t('Payment received') + '</b> #' + bill.bill_no);
        this.fetchData();
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      } finally {
        this.paymentOffcanvas.submitting = false;
      }
    },
    // --- Refund ---
    openRefund(item) {
      if (!this.canRefund(item)) return;
      this.closeOffcanvasInfo();
      this.loadAndOpen(item.id, this.refundModal);
    },
    openRefundById(billId) {
      if (!billId) return;
      this.closeOffcanvasInfo();
      this.loadAndOpen(billId, this.refundModal);
    },
    async openTaxInvoice(item) {
      if (!this.canIssueTaxInvoice(item)) return;
      // ดึงรายละเอียดเต็มก่อน — modal ต้องใช้ชื่อลูกค้า/ยอด/เลขใบย่อที่จะออกแทน
      try {
        const res = await POSBills.info(item.id);
        this.taxInvoiceModal.bill = res.data.data;
        this.taxInvoiceModal.open = true;
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      }
    },
    async handleTaxInvoiceConfirm(payload) {
      const bill = this.taxInvoiceModal.bill;
      if (!bill) return;
      try {
        await POSBills.taxInvoice(bill.id, payload);
        if (this.$refs.taxInvoiceModalRef) this.$refs.taxInvoiceModalRef.hide();
        this.$toast.success('<b>' + this.$t('Tax invoice issued') + '</b> #' + bill.bill_no);
        this.fetchData();
        // พิมพ์ A4 ให้เลย ลูกค้ายืนรออยู่ที่เคาน์เตอร์
        await this.printFullTaxInvoice(bill.id);
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
        if (this.$refs.taxInvoiceModalRef) this.$refs.taxInvoiceModalRef.stopSubmitting();
      }
    },
    async printFullTaxInvoice(billId) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const popup = isMobile ? window.open('', '_blank') : null;
      try {
        const info = await POSBills.info(billId);
        const bill = info.data.data;
        const shopTax = info.data?.filter?.shop_tax || this.shopTax;
        // ที่อยู่/เบอร์/โลโก้ของสาขาอยู่ที่ branch_config ของ endpoint พิมพ์บิล
        let config = {};
        try {
          const res = await POSBills.print(billId);
          config = await preparePrintConfig(res.data?.filter?.branch_config || {});
        } catch (e) {
          config = {};
        }
        await printPdf(buildTaxInvoiceA4DocDefinition({
          bill,
          config,
          shopTax,
          fmt: (v) => this.$storeIndex.formatThaiCurrency(v),
          t: (s) => this.$t(s),
        }), { popup });
      } catch (err) {
        if (popup) popup.close();
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      }
    },
    async handleRefundConfirm(payload) {
      const bill = this.refundModal.bill;
      if (!bill) return;
      try {
        await POSBills.refund(bill.id, {
          refund_reason: payload.refund_reason,
          items: payload.items
        });
        if (this.$refs.refundModalRef) this.$refs.refundModalRef.hide();
        this.$toast.success('<b>' + this.$t('Refunded') + '</b> #' + bill.bill_no);
        this.fetchData();
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
        if (this.$refs.refundModalRef) this.$refs.refundModalRef.stopSubmitting();
      }
    },
    // --- Print ---
    quickPrint(item) {
      this.printBill(item.id);
    },
    quickPrintById(billId) {
      if (!billId) return;
      this.printBill(billId);
    },
    async printBill(id) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const popup = (isMobile && !escposIsSupported()) ? window.open('', '_blank') : null;
      this.printingId = id;
      try {
        const res = await POSBills.print(id);
        const apiBill = res.data?.data || {};
        const branchConfig = res.data?.filter?.branch_config || {};
        const configuration = await preparePrintConfig(branchConfig);
        // ค่าภาษีเป็นของร้าน ไม่ใช่ของสาขา (api/docs/vat-compliance.md §2)
        const shopTax = res.data?.filter?.shop_tax || {};
        const bill = this.toNormalizedBill(apiBill);

        const lastPromptPay = (apiBill.payments || []).find(p => p && p.promptpay_no);
        const promptpayMethod = (configuration.show_qrcode_promptpay && lastPromptPay) ? lastPromptPay : null;
        const promptpayQrDataUrl = promptpayMethod
          ? await buildPromptPayQrImage(promptpayMethod, bill.grand_total)
          : '';
        const reviewQrDataUrl = (configuration.show_qrcode_review && configuration.review_url)
          ? await buildReviewQrImage(configuration.review_url)
          : '';

        const docOpts = {
          bill,
          configuration,
          shopTax,
          promptpayQrDataUrl,
          reviewQrDataUrl,
          promptpayMethod,
          fmt: (v) => this.$storeIndex.formatThaiCurrency(v),
          t: (s) => this.$t(s)
        };

        if (escposIsSupported()) {
          try {
            const canvas = await renderDocDefToCanvas(
              buildReceiptDocDefinition({ ...docOpts, padShortPage: false }),
              { widthPx: dotsForPaperWidth(configuration.bill_paper_width) }
            );
            await printCanvas(canvas);
            return;
          } catch (e) {
            console.warn('WebUSB bill print failed; falling back to PDF:', e);
          }
        }
        await printPdf(buildReceiptDocDefinition(docOpts), { popup });
      } catch (err) {
        if (popup) popup.close();
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      } finally {
        this.printingId = null;
      }
    },
    toNormalizedBill(api) {
      const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      return {
        bill_no:             api.bill_no,
        // '' unless the API says this receipt is the abbreviated tax invoice
        abbreviated_tax_invoice_no: api.abbreviated_tax_invoice_no || '',
        // ยังไม่จ่าย = ใบแจ้งรายการ ไม่ใช่เอกสารภาษี (billReceipt.js เปลี่ยนหัวบิลให้)
        payment_status: api.payment_status || '',
        // เลขประจำเครื่องจากสรรพากร + ช่องทางขาย — ต้องพิมพ์บนใบกำกับอย่างย่อ
        // (api/docs/vat-compliance.md D10) ว่างได้ทั้งคู่
        pos_device_code: api.pos_device_code || '',
        delivery_platform: api.delivery_platform || '',
        // วันที่ปิดการขาย — ใช้เป็นวันที่บนใบกำกับภาษี (พิมพ์ซ้ำต้องได้วันเดิม)
        closed_at:           api.closed_at || '',
        table_name:          api.table_name,
        customer_name:       api.customer_name,
        customer_phone:      api.customer_phone,
        tax_policy:          api.tax_policy,
        subtotal:            toNum(api.sub_total),
        discount:            toNum(api.discount),
        shipping_price:      toNum(api.shipping_price),
        service_charge:      toNum(api.service_charge_amount),
        service_charge_rate: toNum(api.service_charge_rate),
        total_before_vat:    toNum(api.total_before_vat),
        vat:                 toNum(api.total_vat),
        vat_rate:            toNum(api.vat_rate),
        grand_total:         toNum(api.total),
        items: (api.items || []).map(it => ({
          name:               it.name,
          unit_price:         toNum(it.unit_price),
          quantity:           toNum(it.quantity),
          cancelled_quantity: toNum(it.cancelled_quantity),
          subtotal:           toNum(it.subtotal),
          discount:           toNum(it.discount),
          total:              toNum(it.total),
          remark:             it.remark || '',
          is_reward:          !!it.is_reward,
          modifiers: (it.options || []).map(o => ({ name: o.name, price: 0 }))
        }))
      };
    },
    // --- Void ---
    quickVoid(item) {
      if (!this.canVoid(item)) return;
      this.confirmVoid(item.id, item.bill_no);
    },
    quickVoidById(billId) {
      if (!billId) return;
      this.confirmVoid(billId, '');
    },
    confirmVoid(id, billNo) {
      // ปิด info offcanvas ก่อน — ไม่งั้น focus-trap ของ offcanvas จะแย่ง focus
      // ทำให้พิมพ์เหตุผลใน SweetAlert ไม่ได้
      this.closeOffcanvasInfo();
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('Void Bill') + ' ' + (billNo ? '#' + billNo : '') + '?',
        html: this.$t('This action cannot be undone.'),
        input: 'text',
        inputPlaceholder: this.$t('Reason') + '...',
        inputValidator: v => !v ? this.$t('Reason') + ' ' + this.$t('is required') : null,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Void'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary me-2'
        },
        reverseButtons: true
      }).then(async res => {
        if (!res.isConfirmed) return;
        try {
          await POSBills.void(id, { void_reason: res.value });
          this.closeOffcanvasInfo();
          this.$toast.success('<b>' + this.$t('Voided') + '</b> ' + (billNo ? '#' + billNo : ''));
          this.fetchData();
        } catch (err) {
          this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
        }
      });
    },
  },
  watch: {
    'datatable.serverOptions'(nval) {
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      this.fetchData();
    }
  }
};
</script>

<style scoped>
.summary-card {
  background: #fff;
}

.search-input-wrapper {
  min-width: 240px;
  max-width: 360px;
}
.search-input-wrapper .form-control,
.search-input-wrapper .input-group-text {
  box-shadow: none;
}

.source-pill {
  background: rgba(0, 0, 0, 0.06);
  color: #495057;
  font-weight: 600;
}

/* POS-styled datatable container */
.pos-table-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 6px 12px;
}

.pos-table-card :deep(.pos-datatable) {
  --easy-table-border: none;
  --easy-table-row-border: 1px solid #f1f3f5;
  --easy-table-header-font-size: 13px;
  --easy-table-header-height: 50px;
  --easy-table-header-font-color: #6c757d;
  --easy-table-header-background-color: #fff;
  --easy-table-body-row-font-size: 14px;
  --easy-table-body-row-height: 56px;
  --easy-table-body-row-hover-background-color: #fff6ef;
  --easy-table-footer-background-color: #fff;
  --easy-table-footer-font-color: #6c757d;
}

.pos-table-card :deep(.custom-actions) {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pos-table-card :deep(.btn-icon) {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
</style>
